import { Dispatch, SetStateAction } from "react";
import {
  UseComboboxProps as UseDownshiftComboboxProps,
  UseMultipleSelectionProps as UseDownshiftMultipleSelectionProps,
} from "downshift";

import { Avatar, AvatarProps } from "../Avatar";
import { childrenToString } from "../../internal/functions";
import { DataTrackingId, DistributiveOmit } from "../../types";
import { FieldLabelProps } from "../FieldLabel";
import { Icon, IconProps } from "../Icon";
import { useLayoutPropsUtil } from "../../internal/hooks";
import { useTrackingId } from "../../hooks";

import {
  SelectTriggerBase,
  SelectTriggerBaseProps,
} from "./internal/SelectTriggerBase";
import {
  defaultItemToKey,
  defaultItemToString,
} from "../Combobox/internal/ComboboxUtils";

export type SelectTriggerProps<Item> = DistributiveOmit<
  SelectTriggerBaseProps<Item>,
  "prefix" | "selectedItem" | "selectedItems" | "onChange"
> &
  Pick<FieldLabelProps, "required" | "moreInfo"> &
  Pick<UseDownshiftComboboxProps<Item>, "itemToString" | "itemToKey"> &
  DataTrackingId & {
    prefix?:
      | string
      | { icon: DistributiveOmit<IconProps, "size"> }
      | { avatar: DistributiveOmit<AvatarProps, "size"> };
  } & (
    | {
        multiple?: never;
        onChange?:
          | Dispatch<SetStateAction<Item | null>>
          | ((arg: Item | null) => void);
        selectedItem: UseDownshiftComboboxProps<Item>["selectedItem"];
        selectedItems?: never;
      }
    | {
        multiple: true;
        onChange?:
          | Dispatch<SetStateAction<Item[] | null>>
          | ((arg: Item[] | null) => void);
        selectedItem?: never;
        selectedItems: UseDownshiftMultipleSelectionProps<Item>["selectedItems"];
      }
  );

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: look into unknown
export const SelectTrigger = function <Item = any>(
  props: SelectTriggerProps<Item>,
) {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);

  const {
    className,
    style,
    label,
    size,
    error,
    hint,
    description,
    required,
    moreInfo,
    prefix: prefixProp,
    suffix,
    maxRows,
    selectedItemProps = () => ({}),
    id,
    placeholder,
    disabled: isDisabled,
    readOnly: isReadOnly,
    onClick,
    multiple,
    onChange,
    disableClearSelection,
    itemToString: itemToStringProp,
    itemToKey: itemToKeyProp,
    selectedItem = null,
    selectedItems = [],
    ...rest
  } = componentProps;

  const itemToString = itemToStringProp ?? defaultItemToString<Item>;
  const itemToKey = itemToKeyProp ?? defaultItemToKey<Item>;

  function removeSelectedItem(item: Item) {
    if (multiple) {
      onChange?.(
        selectedItems.filter(
          (selectedItem: Item) => itemToKey(selectedItem) !== itemToKey(item),
        ),
      );
    } else if (itemToKey(selectedItem) === itemToKey(item)) {
      onChange?.(null);
    }
  }

  function reset() {
    if (multiple) {
      onChange?.([]);
    } else {
      onChange?.(null);
    }
  }

  const noClearButton = isDisabled || isReadOnly || disableClearSelection;

  const styleCombined = {
    ...style,
    ...layoutStyles,
  };

  const { aiMark, ...labelPropsWithoutAiMark } = props.labelProps || {};

  const data = {
    label: childrenToString(props.label),
    size: props.size,
    required: props.required,
    moreInfo: props.moreInfo,
    description: childrenToString(props.description),
    hint: props.hint,
    labelProps: labelPropsWithoutAiMark,
  };

  const trackingId = useTrackingId({
    name: "ComboboxSearchField",
    data,
    hasOverride: !!props["data-tracking-id"],
  });

  const prefix =
    prefixProp == null || typeof prefixProp === "string" ? (
      prefixProp
    ) : "icon" in prefixProp ? (
      <Icon {...prefixProp.icon} />
    ) : "avatar" in prefixProp ? (
      <Avatar
        {...prefixProp.avatar}
        size={props.size === "small" ? "small" : "medium"}
      />
    ) : (
      prefixProp
    );

  return (
    <SelectTriggerBase
      data-anv="select-trigger"
      data-tracking-id={trackingId}
      className={className}
      style={styleCombined}
      label={label}
      size={size}
      error={error}
      hint={hint}
      description={description}
      labelProps={{
        aiMark,
        moreInfo,
        required,
      }}
      prefix={prefix}
      suffix={suffix}
      maxRows={maxRows}
      selectedItemProps={selectedItemProps}
      id={id}
      placeholder={placeholder}
      disabled={isDisabled}
      readOnly={isReadOnly}
      itemToString={itemToString}
      removeSelectedItem={removeSelectedItem}
      disableClearSelection={noClearButton}
      selectedItem={selectedItem}
      selectedItems={selectedItems}
      onClearButtonClick={reset}
      inputWrapperProps={{ onClick }}
      inputProps={rest}
    />
  );
};
